export type AuthUser = {
  userId: string
  username: string | null
  displayName: string | null
  isAdmin: boolean
}

type AuthApiError = {
  error?: {
    code?: string
    message?: string
  }
}

type ExchangePending = {
  status: "pending"
  retryAfterMs: number
}

type ExchangeSuccess = {
  user: AuthUser
}

const readErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  try {
    const body = (await response.json()) as AuthApiError
    return body.error?.message ?? fallback
  } catch {
    return fallback
  }
}

export const getCsrfTokenFromDocumentCookie = (): string | null => {
  if (typeof document === "undefined") {
    return null
  }

  const target = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("csrf_token="))

  if (!target) {
    return null
  }

  const value = target.slice("csrf_token=".length)
  return value ? decodeURIComponent(value) : null
}

export const authApi = {
  me: async (): Promise<AuthUser | null> => {
    const response = await fetch("/api/auth/me", {
      method: "GET",
      credentials: "include",
    })

    if (response.status === 401) {
      return null
    }

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load session"),
      )
    }

    const payload = (await response.json()) as { user: AuthUser }
    return payload.user
  },

  challenge: async (): Promise<{ challengeId: string; expiresAt: string }> => {
    const response = await fetch("/api/auth/challenge", {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({}),
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to start login challenge"),
      )
    }

    const payload = (await response.json()) as {
      challenge: { id: string; expiresAt: string }
      instructions: { command: string }
    }

    return {
      challengeId: payload.challenge.id,
      expiresAt: payload.challenge.expiresAt,
    }
  },

  exchange: async (input: {
    challengeId: string
    csrfToken: string
  }): Promise<ExchangePending | ExchangeSuccess> => {
    const response = await fetch("/api/auth/exchange", {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": input.csrfToken,
      },
      body: JSON.stringify({ challengeId: input.challengeId }),
    })

    if (response.status === 202) {
      const payload = (await response.json()) as ExchangePending
      return payload
    }

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to exchange login"),
      )
    }

    return (await response.json()) as ExchangeSuccess
  },

  logout: async (): Promise<void> => {
    const csrfToken = getCsrfTokenFromDocumentCookie()
    if (!csrfToken) {
      throw new Error("Missing CSRF token")
    }

    const response = await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": csrfToken,
      },
    })

    if (!response.ok && response.status !== 204) {
      throw new Error(await readErrorMessage(response, "Failed to logout"))
    }
  },
}
